<template>
    <div :class="[{ disabled: isLoading }, 'position-relative']">
        <div
            v-if="isLoading"
            class="spinner-holder"
        >
            <ui-spinner />
        </div>

        <h2 class="mb-4">
            {{ $t('patients.modules.outcome') }}
        </h2>

        <div class="row row-lg-large">
            <div class="col-md-3 mb-4">
                <span class="d-block mb-3 font-weight-bold text-gray-nickel text-uppercase text-smaller">
                    {{ $t('patients.modules.active') }}
                </span>
                <p v-if="!active.length">
                    <small class="font-weight-semi-bold">
                        {{ $t('patients.modules.no_active_modules') }}
                    </small>
                </p>
                <ul class="list mb-n2">
                    <li
                        v-for="(module, index) in active"
                        :key="`module-active-${index}`"
                        class="mb-2"
                    >
                        <button
                            @click="fetchModule(module)"
                            :class="
                                [
                                    {
                                        'btn-primary': module.activeState,
                                    },
                                    `
                                        btn
                                        btn-outline-primary
                                        flex-column
                                        align-items-start
                                        justify-content-start
                                        px-3
                                        w-100
                                    `
                                ]
                            "
                        >
                            <span class="d-flex align-items-center">
                                <span
                                    :style="{ backgroundColor: getColorByScore(module.score) }"
                                    class="
                                        flex-0-0-auto
                                        mr-2
                                        text-center
                                        text-small
                                        text-white
                                        rounded-circle
                                        score
                                    "
                                ></span>
                                <span
                                    :class="[{ 'text-white': module.activeState }, 'score-name']"
                                >
                                    {{ module.name }}
                                </span>
                            </span>
                        </button>
                    </li>
                </ul>

                <span class="d-block mt-6 mb-3 font-weight-bold text-gray-nickel text-uppercase text-smaller">
                    {{ $t('patients.modules.inactive') }}
                </span>
                <ul class="list mb-n2">
                    <li
                        v-for="(module, index) in inactive"
                        :key="`module-inactive-${index}`"
                        :class="[{ disabled: !module.clickable }, 'mb-2']"
                    >
                        <button
                            @click="fetchModule(module)"
                            class="
                                btn
                                btn-outline-primary
                                flex-column
                                align-items-start
                                justify-content-start
                                px-3
                                w-100
                            "
                        >
                            <span class="d-flex align-items-center">
                                <span
                                    v-if="module.score > 0"
                                    :style="{ backgroundColor: getColorByScore(module.score) }"
                                    class="
                                        flex-0-0-auto
                                        mr-2
                                        text-center
                                        text-small
                                        text-white
                                        rounded-circle
                                        score
                                    "
                                ></span>
                                <span class="score-name">
                                    {{ module.name }}

                                    <span v-if="module.advised" class="badge badge-primary mt-1">
                                        {{ $t('patients.modules.advised') }}
                                    </span>
                                </span>
                            </span>
                        </button>
                    </li>
                </ul>

                <button
                    v-if="canUpdate"
                    @click="settingsOpen"
                    class="
                        d-flex
                        align-items-center
                        text-smaller
                        link
                        settings
                    "
                >
                    <ui-icon
                        icon="edit"
                        class="mr-2"
                    />
                    {{ $t('patients.modules.settings_change') }}
                </button>
            </div>

            <div class="col-md-6 mb-4">
                <div
                    v-if="activeModule"
                    class="
                        d-flex
                        flex-column
                        h-100
                        p-4
                        px-xl-5
                        py-xl-6
                        bg-info
                        rounded
                        chart
                    "
                >
                    <h3 class="mb-3">
                        {{ activeModule.name }}
                    </h3>

                    <div>
                        <div
                            class="d-flex flex-column position-absolute"
                            style="top: 50%; width: 12px; margin-top: -57px;"
                        >
                            <span
                                v-for="i in 7"
                                :key="i"
                                :style="{ backgroundColor: getColorByScore(i) }"
                                :class="[
                                    {
                                        'rounded-top': i === 1,
                                        'rounded-bottom': i === 7,
                                    },
                                    `
                                        p-2
                                        text-small
                                        text-white
                                        font-weight-bold
                                    `
                                ]"
                            />
                        </div>

                        <widget-bar-chart
                            v-if="moduleData.chart"
                            @barClick="fetchModuleBar"
                            :data="moduleData.chart"
                            :is-interactive="true"
                            :max="7"
                            :show-y-axis="false"
                        />

                        <p
                            class="mt-3"
                            v-if="moduleData.description"
                            v-html="moduleData.description"
                        />
                    </div>
                </div>
            </div>

            <div class="col-md-3 mb-4">
                <div
                    v-if="moduleBarData.results"
                    class="d-flex flex-column h-100 py-4 py-xl-6"
                >
                    <h3 class="mb-3">
                        {{ $t('patients.modules.based_on') }}
                    </h3>
                    <div
                        v-for="(result, index) in moduleBarData.results"
                        :key="`result-${index}`"
                        class="mb-4"
                    >
                        <p>
                            <span class="d-flex align-items-center justify-content-between">
                                {{ result.name }}

                                <ui-icon
                                    icon="info"
                                    class="flex-0-0-auto ml-2"
                                    :content="result.tooltip"
                                    v-tippy
                                />
                            </span>
                            <strong>{{ result.value }}</strong>
                        </p>
                    </div>

                    <p
                        v-html="moduleBarData.questionnaire"
                        class="mt-auto mb-n4 mb-xl-n5 text-smallest text-gray-nickel"
                    />
                </div>
            </div>
        </div>

        <portal to="portal">
            <ui-modal
                v-if="modal === 'moduleSettings'"
                :title="$t('patients.modules.settings_change')"
            >
                <div class="d-flex flex-column align-items-start">
                    <ul class="w-100 list">
                        <li
                            v-for="(module, index) in settings"
                            :key="`module-setting-${index}`"
                            class="mb-3"
                        >
                            <ui-switch
                                v-model="module.active"
                                :label="module.name"
                            />
                        </li>
                    </ul>

                    <button
                        @click="settingsSave"
                        class="btn btn-primary mt-4"
                    >
                        <span>
                            {{ $t('general.save') }}
                        </span>
                    </button>
                </div>
            </ui-modal>
        </portal>
    </div>
</template>

<script>
import axios from 'axios';

import { mapMutations, mapState } from 'vuex';

import sleep from '../../helpers/sleep';

import UiIcon from '../ui/icon.vue';
import UiModal from '../ui/modal.vue';
import UiSpinner from '../ui/spinner.vue';
import UiSwitch from '../ui/switch.vue';
import WidgetBarChart from '../widgets/bar-chart.vue';

export default {
    name: 'ModulesOverview',
    components: {
        UiIcon,
        UiModal,
        UiSpinner,
        UiSwitch,
        WidgetBarChart,
    },
    props: {
        patientId: {
            type: Number,
            default: null,
        },
        url: {
            type: String,
            default: null,
        },
        canUpdate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLoading: false,
            moduleBarData: [],
            moduleData: [],
            modules: [],
            settings: [],
        };
    },
    created() {
        this.fetch();
    },
    computed: {
        ...mapState('modal', [
            'modal',
        ]),

        active() {
            return this.modules.filter(item => item.active);
        },

        activeModule() {
            return this.modules.find(item => item.activeState);
        },

        inactive() {
            return this.modules.filter(item => !item.active);
        },
    },
    methods: {
        ...mapMutations('modal', [
            'setModal',
        ]),

        async fetch() {
            if (!this.url) return;

            this.isLoading = true;

            const { data } = await axios.get(this.url);

            this.modules = data;
            this.modules = this.modules.map(item => {
                return {
                    activeState: false,
                    ...item,
                };
            })

            if (this.active.length) {
                this.fetchModule(this.modules[0], 0);
            }

            await sleep(300);

            this.isLoading = false;
        },

        async fetchModule(module) {
            this.resetData();
            module.activeState = true;

            this.isLoading = true;

            const { data } = await axios.get(this.route('patient.modules.get_module', {
                patient: this.patientId,
                module: this.activeModule.id,
            }));

            this.moduleData = data;

            const getLastModuleChartDataIndex = this.moduleData.chart.datasets[0].data
                .filter(item => item.value !== null).length - 1;

            await this.$nextTick();

            this.fetchModuleBar(getLastModuleChartDataIndex);
            this.isLoading = false;
        },

        async fetchModuleBar(index) {
            const barId = this.moduleData.chart.datasets[0].data[index];

            if (!barId) {
                return;
            }

            this.isLoading = true;

            const { data } = await axios.get(this.route('patient.modules.get_module_bar', {
                patient: this.patientId,
                module: this.activeModule.id,
                bar: barId,
            }));

            this.moduleBarData = data;

            await sleep(300);

            this.isLoading = false;
        },

        resetData() {
            this.modules.forEach(module => module.activeState = false);
            this.moduleBarData = [];
            this.moduleData = [];
        },

        settingsOpen() {
            this.settings = JSON.parse(JSON.stringify(this.modules));
            this.setModal('moduleSettings');
        },

        async settingsSave() {
            try {
                await axios.post(this.route('patient.modules.save', this.patientId), this.settings);

                this.setModal(null);
                this.fetch();
            } finally {
                this.isLoading = false;
            }
        },
    },
}
</script>

<style lang="scss" scoped>
    .btn-primary {
        color: #fff;
    }

    .score {
        display: inline-block;
        width: 20px;
        height: 20px;
        line-height: 20px;

        &-name {
            color: $dark;

            .btn:hover & {
                color: #fff !important; // stylelint-disable-line
            }
        }
    }

    .spinner-holder {
        z-index: 2;
    }

    .chart {
        position: relative;

        &::before {
            @include respond-to(lg) {
                position: absolute;
                top: 50%;
                left: -20px;
                content: '';
                border-top: 20px solid transparent;
                border-right: 20px solid $info;
                border-bottom: 20px solid transparent;
                transform: translateY(-50%);
            }
        }
    }

    .settings {
        @include respond-to(md) {
            position: absolute;
            transform: translateY(10px);
        }
    }
</style>
